<template>
  <div>
    <WrapperDocRef :title="$t('message.refdocs')">
      <template
        v-if="sale.status == 'approved'"
        #content
      >
        <BaseSectionDocRefLink
          :title="`${$t('sidebar.invoice')} (${sale.reference.invoices.length})`"
          :links="INVLinks"
          :createButtonText="$t('newdocuments.in')"
          :showCreateButton="!disabled"
          @onCreate="handleClickCreateINV"
        />
        <BaseSectionDocRefLink
          :title="`${$t('sidebar.goodsissue')} (${sale.reference.goodsissues.length})`"
          :links="GILinks"
          :createButtonText="$t('newdocuments.gi')"
          :showCreateButton="filterOrdersCanBeIssueCount > 0 && !disabled"
          @onCreate="handleClickCreateGI"
        />
      </template>
      <template
        v-else
        #content
      >
        -
      </template>
    </WrapperDocRef>

    <div
      id="invoiceModal"
      class="modal"
      tabindex="-1"
      role="dialog"
      aria-hidden="true"
      data-keyboard="false"
      data-backdrop="static">
      <div
        class="modal-dialog modal-lg-90-percent"
        role="document">
        <div class="modal-content">
          <div class="modal-header">
            <div class="col row no-padding no-margin">
              <div class="col-6 no-padding">
                <h5 class="modal-title">{{ $t('sidebar.invoice') }}</h5>
              </div>
              <div class="col-6 no-padding text-right">
                <template>
                  <h5
                    v-if="!nextInCode"
                    class="modal-title">
                    <i
                      class="fas fa-spinner spin"
                      style="font-size: 15px; position: relative; top: -1px; }"/>
                  </h5>
                  <h5
                    v-else
                    class="modal-title">
                    {{ nextInCode }}
                  </h5>
                </template>
              </div>
            </div>
          </div>
          <div class="modal-body">
            <template>
              <form-invoice
                v-if="formIN_isReady"
                :so="sale"
                :validation="trickValidationIN"
                @submit="onSubmitInvoice"/>
              <span v-else>
                {{ $t('message.loading') }}
              </span>
            </template>
          </div>
          <div
            v-if="!$store.state.app.loading || !nextInCode"
            class="modal-footer">
            <button
              type="button"
              class="btn btn-light"
              @click="closeInvoiceModal()">{{ $t('button.cancel') }}</button>
            <button
              :disabled="!formIN_isReady"
              type="button"
              class="btn btn-web"
              @click="trickValidationIN++">{{ $t('button.save') }}</button>
          </div>
          <div
            v-if="$store.state.app.loading && nextInCode"
            class="modal-footer">
            <button
              type="button"
              class="btn btn-light"
              disabled>{{ $t('button.cancel') }}</button>
            <button
              type="button"
              class="btn btn-web"
              disabled>
              <i class="fas fa-spinner spin"/>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      id="goodsissueModal"
      class="modal"
      tabindex="-1"
      role="dialog"
      aria-hidden="true"
      data-keyboard="false"
      data-backdrop="static">
      <div
        class="modal-dialog modal-lg-90-percent"
        role="document">
        <div class="modal-content">
          <div class="modal-header">
            <div class="col row no-padding no-margin">
              <div class="col-6 no-padding">
                <h5 class="modal-title">{{ $t('sidebar.goodsissue') }}</h5>
              </div>
              <div class="col-6 no-padding text-right">
                <template>
                  <h5
                    v-if="!nextGiCode"
                    class="modal-title">
                    <i
                      class="fas fa-spinner spin"
                      style="font-size: 15px; position: relative; top: -1px; }"/>
                  </h5>
                  <h5
                    v-else
                    class="modal-title">
                    {{ nextGiCode }}
                  </h5>
                </template>
              </div>
            </div>
          </div>
          <div class="modal-body">
            <template>
              <form-goodsissue
                v-if="formGI_isReady"
                :so="sale"
                :validation="trickValidationGI"
                @submit="onSubmitGoodsIssue"/>
              <span v-else>
                {{ $t('message.loading') }}
              </span>
            </template>
          </div>
          <div
            v-if="!$store.state.app.loading || !nextGiCode"
            class="modal-footer">
            <button
              type="button"
              class="btn btn-light"
              @click="closeGoodsIssueModal()">{{ $t('button.cancel') }}</button>
            <button
              :disabled="!formGI_isReady"
              type="button"
              class="btn btn-web"
              @click="trickValidationGI++">{{ $t('button.save') }}</button>
          </div>
          <div
            v-if="$store.state.app.loading && nextGiCode"
            class="modal-footer">
            <button
              type="button"
              class="btn btn-light"
              disabled>{{ $t('button.cancel') }}</button>
            <button
              type="button"
              class="btn btn-web"
              disabled>
              <i class="fas fa-spinner spin"/>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { toISODateString } from '@/helpers'
import _ from 'lodash'
import WrapperDocRef from '@/components/combine/WrapperDocRef.vue'
import BaseSectionDocRefLink from '@/components/base/BaseSectionDocRefLink.vue'

export default {
  props: {
    sale: {
      type: Object,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      trickValidationIN: 0,
      trickValidationGI: 0,

      showFormIN: false,
      showFormGI: false,
    }
  },
  components: {
    WrapperDocRef,
    BaseSectionDocRefLink,
  },
  computed: {
    ...mapGetters({
      loading: 'getLoading',
      error: 'getError',
      warehouses: 'getWarehouses',
      products: 'getProducts',
      getMainWarehouse: 'getMainWarehouse',
      getMainStock: 'getMainStock',
      nextGiCode: 'getNextGiCode',
      nextInCode: 'getNextInCode',
      getWarehouseInfo: 'getWarehouseInfo',
      getInvoiceStatus: 'getInvoiceStatus',
      getGoodsIssueStatus: 'getGoodsIssueStatus',
      getProductInfoBySkuId: 'getProductInfoBySkuId',
      me: 'getMe',
    }),
    INVLinks() {
      return this.sale.reference.invoices.map((item) => {
        return {
          to: {
            name: 'AccountsInvoiceEdit',
            query: {
              in_id: item.in_id,
              in_code: item.in_code,
            },
          },
          text: item.in_code,
          status: {
            text: this.getInvoiceStatus(item, this.$t, this.mixin).message,
            color: this.getColorFormStatus(this.getInvoiceStatus(item, this.$t, this.mixin).status),
            type: 'icon',
          },
          to_appid: 12,
          current_appid: 4,
        }
      })
    },
    GILinks() {
      return this.sale.reference.goodsissues.map((item) => {
        return {
          to: {
            name: 'GoodsIssueEdit',
            query: {
              gi_id: item.gi_id,
              gi_code: item.gi_code,
            },
          },
          text: item.gi_code,
          status: {
            text: this.getGoodsIssueStatus(item, this.$t).message,
            color: this.getColorFormStatus(this.getGoodsIssueStatus(item, this.$t).status),
            type: 'icon',
          },
          to_appid: 3,
          current_appid: 4,
        }
      })
    },
    formGI_isReady () {
      return this.warehouses.length > 0 && this.showFormGI
    },
    formIN_isReady () {
      return this.showFormIN
    },
    filterOrdersCanBeIssueCount () {
      let count = 0
      this.sale.orders.map(order => {
        let productInfo = this.getProductInfoBySkuId(order.sku_id)
        let skuInfo = this.getSku(order.sku_id)
        if (productInfo) {
          if (productInfo.type == 'product' && skuInfo.stocks.length > 0) {
            count++
          }
        }
      })
      return count
    },
  },
  methods: {
    ...mapActions([
      'getWarehouses',
      'getProducts',
      'insertGoodsissue',
      'getSale',
      'getNextGiCode',
      'getNextInCode',
      'doneGoodsissue',
      'insertInvoice',
    ]),
    getSku (sku_id) {
      let product = this.getProductInfoBySkuId(sku_id)
      return product.prices.find(sku => sku.sku_id == sku_id)
    },
    async openGoodsIssueModal () {
      // set status form
      this.showFormGI = true
      // show modal
      $('#goodsissueModal').modal('show')
      // get next code
      if (!this.nextGiCode) {
        // get warehouses
        await this.getWarehouses()
        // get next code
        await this.getNextGiCode({
          warehouse_id: this.getMainWarehouse.warehouse_id,
          short_name: this.getMainWarehouse.short_name,
        })
        // finish bar loading
        this.showFinish(this.$Progress)
      }
    },
    closeGoodsIssueModal () {
      // set status form
      this.showFormGI = false
      // show modal
      $('#goodsissueModal').modal('hide')
    },
    async openInvoiceModal () {
      // set status form
      this.showFormIN = true
      // show modal
      $('#invoiceModal').modal('show')
      // get next code
      await this.getNextInCode()
      // finish bar loading
      this.showFinish(this.$Progress)
    },
    closeInvoiceModal () {
      // set status form
      this.showFormIN = false
      // show modal
      $('#invoiceModal').modal('hide')
    },
    getDataSetOrders (orders) {
      const data = _.cloneDeep(orders)
      const newData = []
      // remove key, data we don't need
      for (const i in data) {
        const item = data[i]
        if (item.sku_id) {
          newData.push({
            item: item.item,
            description: item.description,
            sku_id: item.sku_id,
            quantity: parseFloat(item.quantity) || 0,
            price: parseFloat(item.price) || 0,
            cost: item.cost != null ? parseFloat(item.cost) : null,
            discounts: item.discounts.map((discount) => ({
              ...discount,
              type: 'general',
              value_amount: parseFloat(discount.value_amount),
            })),
            taxes: item.taxes.map((tax) => ({
              name: tax.tax_name,
              type: tax.type,
              amount: tax.amount,
              form_type: tax.form_type,
              group_name: tax.group_name,
              is_display_fee_result: tax.is_display_fee_result,
              is_for_sale: tax.is_for_sale,
              is_for_purchase: tax.is_for_purchase,
              fee: tax.fee ? tax.fee : null,
              tax_id: tax.tax_id,
            })),
          })
        }
      }
      return newData
    },
    async onSubmitInvoice (isValid, data) {
      if (!isValid) {
        return false
      }

      let creditType = 'cash'
      let creditDays = 0

      if (data.selectedCredit == 'custom') {
        creditType = 'days'
        creditDays = data.inputCustomCreditDay
      } else if (data.selectedCredit.search('days.') >= 0) {
        let arr = data.selectedCredit.split('.')
        let days = arr[1]
        creditType = 'days'
        creditDays = days
      }

      const orders = this.getDataSetOrders(data.orders)
      const dataSet = {
        'orders': orders,
        'customer_id': this.sale.customer ? this.sale.customer.customer_id : null,
        'status': data.approved ? 'approved' : 'padding',
        'created_at': data.created_at.toISOString(),
        'sale_type': data.sale_type,
        'credit': {
          	'type': creditType,
          	'days': parseInt(creditDays),
        },
        'scheduled_at': data.scheduled_at.toISOString(),
        'note': data.note,
        'so_id': this.sale.so_id,
      }

      const res = await this.insertInvoice(dataSet)

      if (res.success) {
        const newInvoice = res.data.invoice

        // call api to get sale
        await this.getSale({ so_id: this.sale.so_id })

        // show notify
        this.notify(this.$vs, {
          text: this.$t('message.hasbeencreated', { msg: newInvoice.in_code }),
          color: 'success',
          icon: 'cloud',
        })

        // finish loading
        this.showFinish(this.$Progress)
        this.closeInvoiceModal()
      }
    },
    async onSubmitGoodsIssue (isValid, data) {
      if (!isValid) {
        this.notify(this.$vs, {
          text: this.$t('message.notfoundorders'),
          color: 'warning',
          icon: 'warning',
          time: 3000,
        })
        return false
      }

      let warehouseInfo = this.getWarehouseInfo(data.warehouse_id)
      let saleOrders = data.orders

      // set dataset to call api
      var goodsissueOrders = []
      for (let i in saleOrders) {
        let order = saleOrders[i]
        let productInfo = this.getProductInfoBySkuId(order.sku_id)
        let skuInfo = this.getSku(order.sku_id)
        let stock = skuInfo.stocks.find(stock => stock.warehouse_id == data.warehouse_id)

        if (stock) {
          const newOrder = {
            'sku_id': order.sku_id,
            'item': order.item,
            'quantity': Number(order.quantity),
            'stock_id': stock.stock_id,
            'warehouse_id': stock.warehouse_id,
            'bin_location_id': order.bin_location_id,
          }
          if (order.use_lot) {
            newOrder.lot_numbers = order.lot.selected.map((lot) => {
              return {
                'lot_id': lot.lotId,
                'lot_code': lot.lotCode,
                'bin_location_id': lot.binId,
                'unit': lot.unit,
              }
            })
          }
          if (order.use_serial) {
            newOrder.serial_numbers = order.serial.selected.map((ser) => {
              return {
                'bin_location_id': ser.binId,
                'ser_id': ser.serId,
                'ser_code': ser.serCode,
                'serial_number': ser.serialNumber,
              }
            })
          }
          goodsissueOrders.push(newOrder)
        }
      }

      let dataSet = {
        'orders': goodsissueOrders,
        'stockout': {
          'warehouse_id': warehouseInfo.warehouse_id,
          'short_name': warehouseInfo.short_name,
        },
        'note': data.note,
        'so_id': this.sale.so_id,
        'customer_id': this.sale.customer ? this.sale.customer.customer_id : null,
        'scheduled_at': data.scheduled_at.toISOString(),
      }

      // call api to insert goodsissues
      let res = await this.insertGoodsissue(dataSet)

      if (res.success) {
        let goodsissue = res.data.goodsissue

        // done goodsissue if user checked "received"
        if (data.delivered) {
          let doneOrders = []
          for (let i in res.data.orders) {
            let order = res.data.orders[i]
            let newOrder = {
              'gio_id': order._id,
              'sku_id': order.sku_id,
              'stock_id': order.stock_id,
              'warehouse_id': order.wh_id,
              'done': order.quantity,
              'quantity': order.quantity,
            }
            // add lot numbers
            if (order.lot_numbers && order.lot_numbers.length) {
              newOrder.lot_numbers = order.lot_numbers.map((lot) => {
                return {
                  'lot_id': lot.lot_id,
                  'lot_code': lot.lot_code,
                  'bin_location_id': lot.bin_location_id,
                  'unit': Number(lot.unit),
                }
              })
            }
            // add serial numbers
            if (order.serial_numbers && order.serial_numbers.length) {
              newOrder.serial_numbers = order.serial_numbers.map((ser) => {
                return {
                  'bin_location_id': ser.bin_location_id,
                  'ser_id': ser.ser_id,
                  'ser_code': ser.ser_code,
                  'serial_number': ser.serial_number,
                }
              })
            }
            doneOrders.push(newOrder)
          }
          let dataSet_ = {
            'gi_id': goodsissue._id,
            'gi_code': goodsissue.gi_code,
            // "note": data.note,
            'orders': doneOrders,
            'reference': {
              'so': {
                'id': this.sale.so_id,
                'code': this.sale.so_code,
              },
            },
            'respond_by': this.me.user_id,
            'posting_at': new Date().toISOString(),
          }
          let res_ = await this.doneGoodsissue(dataSet_)
        }

        // call api to get sale
        await this.getSale({ so_id: this.sale.so_id })

        // show notify
        this.notify(this.$vs, {
          text: this.$t('message.hasbeencreated', { msg: goodsissue.gi_code }),
          color: 'success',
          icon: 'cloud',
        })

        if (data.delivered) {
          let that = this
          // show notify
          setTimeout(() => {
            this.notify(that.$vs, {
              text: that.$t('message.hasbeencompleted', { msg: goodsissue.gi_code }),
              color: 'success',
              icon: 'check',
            })
          }, 500)
        }

        // finish loading
        this.showFinish(this.$Progress)
        this.closeGoodsIssueModal()
      }
    },
    handleClickCreateINV() {
      this.openInvoiceModal()
    },
    handleClickCreateGI() {
      this.openGoodsIssueModal()
    },
  },
  created: async function () {

  },
  mounted: function () {
    // set popup modal
    this.setModal('#invoiceModal')
    this.setModal('#goodsissueModal')
  },
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/apps/sales/so/include/RefSO.vue